import React, { useMemo, useState } from 'react';
import { Button, Skeleton, Space, Typography } from 'antd';
import { CrudFilters, HttpError, useModal } from '@refinedev/core';
import { DownloadOutlined } from '@ant-design/icons';
import { ENROLLMENT_STATUS } from 'constants/admin/enrollments';
import { EditFrequencyModal } from 'components/modal/professor_area/students/EditFrequencyModal';
import { EmptyTab } from '../EmptyTab';
import { IStudent, IStudentShow } from 'interfaces/professor_area/students';
import { ImportAttendanceModal } from 'components/modal/professor_area/students/ImportAttendanceModal';
import { SearchFilters } from './components/SearchFilters';
import { StudentDetailsModal } from 'components/modal/professor_area/students/StudentDetailsModal';
import { StudentList } from './components/StudentList';
import { useCanMultiple } from 'components/layout/sider';
import { useParams } from 'react-router-dom';
import { useTable } from '@refinedev/antd';
import type { IProfessorAreaClassroom } from 'interfaces/professor_area_classrooms';

export interface IFilter {
  name: string;
  enrollment: string;
}

export const TabStudents: React.FC<{
  classroom: IProfessorAreaClassroom;
}> = ({ classroom }) => {
  const { id } = useParams<{ id: string }>();
  const [studentContext, setStudentContext] = useState<IStudentShow>();
  const editFrequencyModalProps = useModal();
  const studentDetailsModalProps = useModal();
  const attendanceModalProps = useModal();
  const [
    showEnrollmentPermission,
    editEnrollmentPermission,
    forumsPermission,
    discussionThreadsPermission
  ] = useCanMultiple([
    { resource: 'professor_area/classroom_enrollments', action: 'show' },
    { resource: 'professor_area/classroom_enrollments', action: 'edit' },
    { resource: 'professor_area/forums', action: 'list' },
    { resource: 'professor_area/discussion_threads', action: 'list' }
  ]);
  const canShowEnrollment = showEnrollmentPermission.data?.can ?? false;
  const canEditEnrollment = editEnrollmentPermission.data?.can ?? false;
  const canAccessForums = forumsPermission.data?.can ?? false;
  const canAccessDiscussionThreads =
    discussionThreadsPermission.data?.can ?? false;
  const {
    tableQueryResult: { data, isLoading, refetch },
    current,
    setCurrent,
    pageSize,
    searchFormProps
  } = useTable<IStudent, HttpError, IFilter>({
    resource: 'professor_area/classroom_enrollments',
    meta: { classroomId: id },
    initialPageSize: 999,
    filters: {
      permanent: [
        {
          field: 'filter[by_enrollment_status]',
          operator: 'eq',
          value: ENROLLMENT_STATUS.ACTIVE
        }
      ]
    },
    onSearch: ({ name, enrollment }) => {
      const filters: CrudFilters = [];

      filters.push(
        {
          field: 'filter[by_user_name]',
          operator: 'eq',
          value: name
        },
        {
          field: 'filter[by_enrollment_registration]',
          operator: 'eq',
          value: enrollment
        }
      );
      return filters;
    }
  });
  const studentsData = data?.data as IStudent[];

  const hasData = useMemo(() => {
    return studentsData?.length > 0;
  }, [studentsData]);

  const handleEditFrequency = (student: IStudentShow) => {
    setStudentContext(student);
    editFrequencyModalProps.show();
  };

  const handleShowDetails = (student: IStudentShow) => {
    setStudentContext(student);
    studentDetailsModalProps.show();
  };

  if (isLoading) {
    return (
      <Skeleton active paragraph={{ rows: 6 }} className="w-full h-full" />
    );
  }

  const EmptyComponent = () => {
    return (
      <EmptyTab
        title="Nenhum aluno encontrado"
        buttonText={canEditEnrollment ? 'Importar frequência' : undefined}
        onClick={
          canEditEnrollment ? () => attendanceModalProps.show() : undefined
        }
      />
    );
  };

  const ContentComponent = () => {
    return (
      <>
        <SearchFilters searchFormProps={searchFormProps} />
        <Space className="w-full justify-between items-center mb-4">
          <Typography.Title level={5} className="!m-0">
            Listagem de alunos
          </Typography.Title>
          {canEditEnrollment && (
            <Button
              type="default"
              icon={<DownloadOutlined />}
              onClick={attendanceModalProps.show}
            >
              Importar frequência
            </Button>
          )}
        </Space>

        <StudentList
          data={studentsData.slice(
            (current - 1) * pageSize,
            current * pageSize
          )}
          total={studentsData.length}
          pageSize={pageSize}
          currentPage={current}
          onPageChange={setCurrent}
          handleEditFrequency={handleEditFrequency}
          handleShowDetails={handleShowDetails}
          canEditFrequency={canEditEnrollment}
          canShowDetails={canShowEnrollment}
        />
      </>
    );
  };

  return (
    <>
      {hasData ? <ContentComponent /> : <EmptyComponent />}

      {canEditEnrollment && (
        <EditFrequencyModal
          modalProps={editFrequencyModalProps}
          student={studentContext}
          refetch={refetch}
          onUpdateStudent={setStudentContext}
        />
      )}

      {canShowEnrollment && (
        <StudentDetailsModal
          handleEditFrequency={handleEditFrequency}
          modalProps={studentDetailsModalProps}
          student={studentContext}
          classroomCode={classroom.code}
          disciplineName={classroom.discipline_name}
          canEditFrequency={canEditEnrollment}
          canAccessForums={canAccessForums}
          canAccessDiscussionThreads={canAccessDiscussionThreads}
        />
      )}

      {canEditEnrollment && (
        <ImportAttendanceModal
          modalProps={attendanceModalProps}
          refetch={refetch}
          classroomId={id!}
        />
      )}
    </>
  );
};
